import React, { useState, useEffect, useCallback } from "react";
import {
  Zap, Router, CheckCircle, XCircle, RefreshCw, AlertTriangle, Clock,
} from "lucide-react";
import { api } from "../lib/api";
import { useSiteContext } from "../components/layout/Layout";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Button } from "../components/ui/button";

const WRITE_ROLES = ["super_admin", "admin", "customer_admin", "site_manager"];
const OUTPUT_SIGNAL_TYPES = ["digital_output", "binary_output", "analog_output"];

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "medium" });
  } catch {
    return iso;
  }
}

function fmtTimeShort(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("en-IN", { timeStyle: "medium" });
  } catch {
    return iso;
  }
}

// Keep only the most recent reading per parameter name
function dedupeRegisters(docs) {
  const map = new Map();
  for (const doc of docs) {
    const existing = map.get(doc.parameter);
    if (!existing || doc.ingested_at > existing.ingested_at) {
      map.set(doc.parameter, doc);
    }
  }
  return Array.from(map.values()).sort((a, b) => a.parameter.localeCompare(b.parameter));
}

function AckStatusBadge({ cmd }) {
  switch (cmd.ack_state) {
    case "acked_success":
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold">
          <CheckCircle className="w-3.5 h-3.5" strokeWidth={2.5} />
          {cmd.confirmed_via === "telemetry_readback" ? "Confirmed" : "Confirmed"}
        </span>
      );
    case "acked_failure":
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-50 border border-red-200 text-red-700 text-xs font-semibold" title={cmd.device_error || undefined}>
          <XCircle className="w-3.5 h-3.5" strokeWidth={2.5} /> Rejected
          {cmd.device_error && <span className="text-red-600 font-normal text-[10px]">({cmd.device_error})</span>}
        </span>
      );
    case "no_confirmation":
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold">
          <AlertTriangle className="w-3.5 h-3.5" strokeWidth={2.5} /> No ACK
        </span>
      );
    case "pending":
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-sky-50 border border-sky-200 text-sky-700 text-xs font-semibold">
          <Clock className="w-3.5 h-3.5 animate-spin" strokeWidth={2.5} /> Pending…
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-50 border border-red-200 text-red-700 text-xs font-semibold">
          <XCircle className="w-3.5 h-3.5" strokeWidth={2.5} /> Failed
        </span>
      );
  }
}

export default function GatewayCommands() {
  const { selectedSite } = useSiteContext();
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.includes(user?.role);

  const [gateway, setGateway] = useState(null);
  const [status, setStatus] = useState(null);
  const [statusChecked, setStatusChecked] = useState(false);
  const [registers, setRegisters] = useState([]);
  const [cmdLog, setCmdLog] = useState([]);
  const [writableRegs, setWritableRegs] = useState([]);
  const [analogInputs, setAnalogInputs] = useState({}); // tag_name -> typed value, for analog_output rows
  const [confirm, setConfirm] = useState(null); // { tag_name, value, label }
  const [sending, setSending] = useState(false);

  const loadGateway = useCallback(async () => {
    if (!selectedSite) return;
    try {
      const r = await api.get("/hardware/gateways");
      const gw = (r.data || []).find((g) => g.site_id === selectedSite);
      setGateway(gw || null);
    } catch { /* ignore */ }
  }, [selectedSite]);

  const loadStatus = useCallback(async (gwId) => {
    if (!gwId) return;
    try {
      const r = await api.get(`/v1/gateways/${gwId}/status`);
      setStatus(r.data);
    } catch { /* ignore */ } finally {
      setStatusChecked(true);
    }
  }, []);

  const loadTelemetry = useCallback(async () => {
    if (!selectedSite) return;
    try {
      const r = await api.get(`/v1/sites/${selectedSite}/telemetry/latest`);
      setRegisters(dedupeRegisters(r.data || []));
    } catch { /* ignore */ }
  }, [selectedSite]);

  const loadCmdLog = useCallback(async (gwId) => {
    if (!gwId) return;
    try {
      const r = await api.get(`/v1/gateways/${gwId}/command-log`);
      setCmdLog(r.data || []);
    } catch { /* ignore */ }
  }, []);

  const loadWritableRegs = useCallback(async (gwId) => {
    if (!gwId) return;
    try {
      const r = await api.get(`/hardware/gateways/${gwId}/io-mappings`);
      const outputs = (r.data || [])
        .filter((m) => OUTPUT_SIGNAL_TYPES.includes(m.signal_type))
        .map((m) => ({
          tag_name: m.io_point_address,
          readback_tag_name: m.readback_io_point_name || m.io_point_address,
          label: m.io_point_name || m.io_point_address,
          signal_type: m.signal_type,
          unit: m.engineering_unit,
        }));
      setWritableRegs(outputs);
    } catch { /* ignore */ }
  }, []);

  // Reset + load gateway whenever site changes
  useEffect(() => {
    setGateway(null);
    setStatus(null);
    setStatusChecked(false);
    setRegisters([]);
    setCmdLog([]);
    setWritableRegs([]);
    loadGateway();
  }, [loadGateway]);

  // Poll status + telemetry every 5 s once gateway is known; load command log + writable regs once
  useEffect(() => {
    if (!gateway) return;
    loadStatus(gateway.gateway_id);
    loadTelemetry();
    loadCmdLog(gateway.gateway_id);
    loadWritableRegs(gateway.gateway_id);
    const i = setInterval(() => {
      loadStatus(gateway.gateway_id);
      loadTelemetry();
      loadCmdLog(gateway.gateway_id);
    }, 5000);
    return () => clearInterval(i);
  }, [gateway, loadStatus, loadTelemetry, loadCmdLog, loadWritableRegs]);

  const sendCommand = async () => {
    if (!confirm || !gateway) return;
    setSending(true);
    try {
      await api.post(`/v1/gateways/${gateway.gateway_id}/write`, {
        tag_name: confirm.tag_name,
        value: confirm.value,
      });
      const displayVal = confirm.value === "1" ? "ON" : confirm.value === "0" ? "OFF" : confirm.value;
      toast.success(`Command sent — ${confirm.label} set to ${displayVal}`);
      loadCmdLog(gateway.gateway_id);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail === "device_not_connected") {
        toast.error("Device is not connected — command not delivered");
      } else {
        toast.error(detail || "Command failed");
      }
    } finally {
      setSending(false);
      setConfirm(null);
    }
  };

  const currentVal = (tag_name) => {
    const doc = registers.find((r) => r.parameter === tag_name);
    return doc !== undefined ? doc.value : null;
  };

  const connected = status?.connected === true;

  if (!selectedSite) {
    return (
      <div className="p-8 text-gray-500 text-center">
        Select a site to view gateway data.
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="sticky top-0 z-40 bg-white border-b border-gray-200 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-xs text-orange-600 uppercase tracking-wider font-bold flex items-center gap-1">
                <Router className="w-3 h-3" strokeWidth={2.5} /> Gateway Remote Control
              </div>
              <h1 className="text-3xl md:text-4xl font-extrabold mt-1">Remote Command Center</h1>
              <p className="text-sm text-gray-500 mt-1">Monitor live registers &amp; send commands to fire safety equipment</p>
            </div>

            {gateway && status && (
              <div className="flex flex-col items-end gap-3">
                <div
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg font-semibold text-xs ${
                    !statusChecked
                      ? "bg-gray-100 border border-gray-200 text-gray-600"
                      : connected
                      ? "bg-emerald-50 border border-emerald-200 text-emerald-700"
                      : "bg-red-50 border border-red-200 text-red-700"
                  }`}
                >
                  <span
                    className={`w-2.5 h-2.5 rounded-full ${
                      !statusChecked
                        ? "bg-gray-400 animate-pulse"
                        : connected
                        ? "bg-emerald-500 status-pulse-green"
                        : "bg-red-500"
                    }`}
                  />
                  {!statusChecked ? "Checking connection…" : connected ? "Device Connected" : "Device Offline"}
                </div>
                {status && (
                  <div className="text-right text-xs space-y-0.5">
                    <div className="text-gray-900 font-medium">
                      {status.gateway_name || status.hardware_model || gateway.gateway_id}
                    </div>
                    {status.peer_ip && (
                      <div className="text-gray-500">IP: <span className="font-mono">{status.peer_ip}</span></div>
                    )}
                    {status.last_seen_at && (
                      <div className="text-gray-500">
                        Last seen: <span className="font-medium">{fmtTime(status.last_seen_at)}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="p-6 max-w-7xl mx-auto space-y-6">

      {/* ── No gateway registered ── */}
      {!gateway && (
        <div className="glass rounded-lg p-10 text-center text-gray-500">
          <Router className="w-8 h-8 mx-auto mb-3 opacity-30" strokeWidth={2.5} />
          <p>No hardware gateway registered for this site.</p>
          <p className="text-xs mt-1">Add one in Hardware Gateways → Management.</p>
        </div>
      )}

      {gateway && (
        <>
          {/* ── Live Register Values ── */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-sm uppercase tracking-widest font-bold text-gray-700">
                  📊 Live Register Monitor
                </h2>
                <p className="text-xs text-gray-500 mt-1">Real-time data from your device · Auto-refresh every 5 s</p>
              </div>
            </div>

            {registers.length === 0 ? (
              <div className="glass rounded-lg p-12 text-center">
                <RefreshCw className="w-8 h-8 mx-auto mb-3 text-gray-400 animate-spin" strokeWidth={2} />
                <p className="text-gray-600 font-medium">Waiting for device data…</p>
                {!statusChecked ? (
                  <p className="text-xs text-gray-500 mt-1">Checking gateway connection…</p>
                ) : !connected ? (
                  <p className="text-xs text-red-600 mt-1">⚠️ Device is offline — no new data will arrive.</p>
                ) : null}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {registers.map((reg) => (
                  <div
                    key={reg.parameter}
                    className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md hover:border-orange-200 transition space-y-2"
                  >
                    <div className="text-[10px] text-gray-500 font-mono uppercase tracking-wider truncate" title={reg.parameter}>
                      {reg.parameter}
                    </div>
                    <div className="text-2xl font-bold text-gray-900 leading-tight">
                      {reg.value !== null && reg.value !== undefined
                        ? Number.isInteger(reg.value)
                          ? reg.value
                          : Number(reg.value).toFixed(2)
                        : "—"}
                      {reg.unit && (
                        <span className="text-xs font-normal text-gray-500 ml-1">{reg.unit}</span>
                      )}
                    </div>
                    <div className="flex items-center justify-between gap-1 pt-1">
                      <span
                        className={`text-[10px] font-semibold px-2 py-1 rounded-full border ${
                          reg.quality === "GOOD"
                            ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                            : "bg-amber-50 border-amber-200 text-amber-700"
                        }`}
                      >
                        {reg.quality || "—"}
                      </span>
                      <span className="text-[10px] text-gray-500">
                        {fmtTimeShort(reg.timestamp)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* ── Write Command Panel (admin/operator only) ── */}
          {canWrite && (
            <section>
              <div className="mb-4">
                <h2 className="text-sm uppercase tracking-widest font-bold text-gray-700">
                  ⚡ Remote Control Panel
                </h2>
                <p className="text-xs text-gray-500 mt-1">Send commands to actuate relays and control equipment</p>
              </div>

              {!statusChecked ? (
                <div className="bg-white border border-gray-200 rounded-lg p-6 text-center text-gray-600">
                  <RefreshCw className="w-6 h-6 mx-auto mb-2 text-gray-400 animate-spin" strokeWidth={2} />
                  <p>Checking device connection…</p>
                </div>
              ) : !connected ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                  <div className="flex items-center gap-3">
                    <XCircle className="w-5 h-5 text-red-600 shrink-0" strokeWidth={2.5} />
                    <div>
                      <p className="text-red-900 font-semibold">Device Offline</p>
                      <p className="text-red-700 text-sm mt-0.5">Commands are disabled until the device reconnects.</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                    <p className="text-emerald-700 font-semibold text-sm">✓ Device connected and ready for commands</p>
                  </div>
                </div>
              )}

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" strokeWidth={2.5} />
                  <div>
                    <p className="text-amber-900 font-semibold text-sm">⚠️ Critical Actions</p>
                    <p className="text-amber-800 text-sm mt-1">
                      All commands actuate physical relays on fire safety equipment. Each action is audit-logged and <span className="font-bold">cannot be reversed</span>.
                      Ensure you fully understand the consequences before sending.
                    </p>
                  </div>
                </div>
              </div>

              {connected && !writableRegs.length && (
                <div className="bg-white border border-gray-200 rounded-lg p-8 text-center mt-4">
                  <Zap className="w-8 h-8 mx-auto mb-3 text-gray-300" strokeWidth={2} />
                  <p className="text-gray-600 font-medium">No Writable Registers</p>
                  <p className="text-gray-500 text-sm mt-1">
                    Add output signal types (Digital/Binary/Analog) in Hardware Gateways → IO Mappings
                  </p>
                </div>
              )}

              {connected && writableRegs.length > 0 && (
                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {writableRegs.map((reg) => {
                    const val = currentVal(reg.readback_tag_name);
                    const isAnalog = reg.signal_type === "analog_output";
                    return (
                      <div key={reg.tag_name} className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition">
                        <div className="mb-4">
                          <div className="text-gray-900 font-semibold text-base">{reg.label}</div>
                          <div className="text-xs text-gray-500 font-mono mt-1">{reg.tag_name}</div>
                        </div>

                        <div className="space-y-3">
                          {val !== null && (
                            <div className={`flex items-center justify-between p-2 rounded-lg border ${
                              isAnalog
                                ? "bg-sky-50 border-sky-200"
                                : val === 1
                                ? "bg-emerald-50 border-emerald-200"
                                : "bg-slate-50 border-slate-200"
                            }`}>
                              <span className="text-xs text-gray-600">Current Status</span>
                              <span className={`font-bold text-sm ${
                                isAnalog
                                  ? "text-sky-700"
                                  : val === 1
                                  ? "text-emerald-700"
                                  : "text-slate-700"
                              }`}>
                                {isAnalog ? `${val}${reg.unit ? ` ${reg.unit}` : ""}` : val === 1 ? "ON" : "OFF"}
                              </span>
                            </div>
                          )}

                          {isAnalog ? (
                            <div className="flex gap-2">
                              <input
                                type="number"
                                disabled={!connected}
                                value={analogInputs[reg.tag_name] ?? ""}
                                onChange={(e) => setAnalogInputs({ ...analogInputs, [reg.tag_name]: e.target.value })}
                                placeholder={reg.unit || "value"}
                                className="flex-1 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-sm disabled:opacity-50"
                              />
                              <Button
                                size="sm"
                                disabled={!connected || !analogInputs[reg.tag_name]}
                                onClick={() =>
                                  setConfirm({ tag_name: reg.tag_name, value: String(analogInputs[reg.tag_name]), label: reg.label })
                                }
                                className="bg-sky-600 hover:bg-sky-700 text-white disabled:opacity-50"
                              >
                                Set
                              </Button>
                            </div>
                          ) : (
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                disabled={!connected || val === 1}
                                onClick={() =>
                                  setConfirm({ tag_name: reg.tag_name, value: "1", label: reg.label })
                                }
                                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {val === 1 ? "Already ON" : "Turn ON"}
                              </Button>
                              <Button
                                size="sm"
                                disabled={!connected || val === 0}
                                onClick={() =>
                                  setConfirm({ tag_name: reg.tag_name, value: "0", label: reg.label })
                                }
                                className="flex-1 bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {val === 0 ? "Already OFF" : "Turn OFF"}
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          )}

          {/* ── Command Log ── */}
          <section>
            <div className="mb-4">
              <h2 className="text-sm uppercase tracking-widest font-bold text-gray-700">
                📋 Command Audit Log
              </h2>
              <p className="text-xs text-gray-500 mt-1">All remote commands sent to this device · Last 20 entries</p>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              {cmdLog.length === 0 ? (
                <div className="p-12 text-center">
                  <Zap className="w-8 h-8 mx-auto mb-3 text-gray-300" strokeWidth={2} />
                  <p className="text-gray-600 font-medium">No Commands Sent Yet</p>
                  <p className="text-gray-500 text-sm mt-1">Commands will appear here as you send them</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50 text-gray-700 text-[11px] uppercase tracking-wider">
                        <th className="text-left px-4 py-3 font-semibold">Time</th>
                        <th className="text-left px-4 py-3 font-semibold">Register</th>
                        <th className="text-left px-4 py-3 font-semibold">Value</th>
                        <th className="text-left px-4 py-3 font-semibold">Issued By</th>
                        <th className="text-left px-4 py-3 font-semibold">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cmdLog.slice(0, 20).map((cmd) => (
                        <tr
                          key={cmd.command_id}
                          className="border-b border-gray-200 hover:bg-gray-50 transition"
                        >
                          <td className="px-4 py-3 text-gray-600 font-mono text-xs whitespace-nowrap">
                            {fmtTime(cmd.issued_at)}
                          </td>
                          <td className="px-4 py-3 text-gray-900 font-mono text-xs font-semibold">
                            {cmd.tag_name}
                          </td>
                          <td className="px-4 py-3 text-xs font-bold">
                            <span
                              className={
                                cmd.value === "1" ? "text-emerald-700" : cmd.value === "0" ? "text-red-700" : "text-sky-700"
                              }
                            >
                              {cmd.value === "1" ? "ON" : cmd.value === "0" ? "OFF" : cmd.value}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-600 text-xs">
                            {cmd.issued_by}
                          </td>
                          <td className="px-4 py-3">
                            <AckStatusBadge cmd={cmd} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </section>
        </>
      )}

      {/* ── Confirmation AlertDialog ── */}
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && !sending && setConfirm(null)}>
        <AlertDialogContent className="bg-white border-gray-200 text-gray-900">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Relay Command</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-500 space-y-2">
              <span className="block">
                Send{" "}
                <code className="bg-gray-100 text-gray-900 px-1.5 py-0.5 rounded text-xs">
                  {confirm?.tag_name} = {confirm?.value}
                </code>{" "}
                to{" "}
                <span className="text-gray-900 font-medium">
                  {status?.gateway_name || gateway?.gateway_id}
                </span>
                ?
              </span>
              <span className="block text-amber-400/80">
                This will actuate a physical relay on the fire panel. The action is
                irreversible and audit-logged.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="border-gray-200 text-gray-600 hover:text-gray-900 bg-transparent"
              onClick={() => setConfirm(null)}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={sending}
              onClick={sendCommand}
              className="bg-red-600 hover:bg-red-700 text-white border-0 disabled:opacity-60"
            >
              {sending ? "Sending…" : "Send Command"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      </div>
    </div>
  );
}
